import { useEffect, useState } from 'react';

import { fetchBarrierGeometry } from '@/api/endpoints/barriers';
import { useTileRevision } from '@/stores/TileRevisionContext';
import type { BarrierGeoJsonFeature } from '@/types/barriers';
import { isAxios404 } from '@/utils/axiosError';

export type BarrierGeometryState =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | { readonly status: 'success'; readonly feature: BarrierGeoJsonFeature }
  | { readonly status: 'error'; readonly message: string }
  | { readonly status: 'not_found' };

export function useBarrierGeometry(id: number | null): BarrierGeometryState {
  const [state, setState] = useState<BarrierGeometryState>({ status: 'idle' });
  const { barriersRevision } = useTileRevision();

  useEffect(() => {
    if (id === null) {
      setState({ status: 'idle' });
      return undefined;
    }
    let cancelled = false;
    setState({ status: 'loading' });
    fetchBarrierGeometry(id)
      .then((feature) => {
        if (!cancelled) {
          setState({ status: 'success', feature });
        }
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return;
        }
        if (isAxios404(error)) {
          setState({ status: 'not_found' });
          return;
        }
        setState({
          status: 'error',
          message: error instanceof Error ? error.message : 'unknown error',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [id, barriersRevision]);

  return state;
}
